import React, { useState, useCallback, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Button, Textarea, Label } from '@iq/react-components';

import { updateAlarm } from '../../../../../bundles/alarms';
import { getTimezone } from '../../../../../bundles/application';
import { getHasPermission } from '../../../../../bundles/auth';
import { getActiveSite } from '../../../../../bundles/sites';
import { requestUser } from '../../../../../bundles/ad';
import { formatDate, UserSignature } from '../../utils';

import './AlarmHandlerTab.scss';

const ALARM_STATE = {
  NEW: 'new',
  ACKNOWLEDGED: 'acknowledged',
  RESOLVED: 'resolved',
};

// SUB-COMPONENTS
const ResolutionContent = ({ alarm, timezone }) => (
  <div className="handler-section">
    <div className="alarm-info-item">
      <Label>Resolved by</Label>
      <span className="alarm-info-value">
        <UserSignature userId={alarm.resolvedBy} />
      </span>
    </div>
    <div className="alarm-info-item">
      <Label>Resolved at</Label>
      <span className="alarm-info-value">{formatDate(alarm.resolvedAt, timezone)}</span>
    </div>
    <div className="alarm-info-item">
      <Label>Resolution notes</Label>
      <p className="handler-text-block">{alarm.resolvedText}</p>
    </div>
  </div>
);

const MessageSection = ({ alarm, severityLabel }) => {
  // ADDING IT HERE BECAUSE NOT SURE IF EVERY ALARM 100% HAS A MESSAGE
  if (!alarm?.message) return null;

  return (
    <div className="handler-section">
      <Label>{severityLabel}</Label>
      <p className="handler-text-block">{alarm.message}</p>
    </div>
  );
};

const RecommendationSection = ({ alarm }) => {
  if (!alarm?.recommendation) return null;

  return (
    <div className="handler-section">
      <Label>Recommendation based on Latest Severity</Label>
      <p className="handler-text-block">{alarm.recommendation}</p>
    </div>
  );
};

const AcknowledgementSection = ({ alarm, timezone }) => {
  if (!alarm?.acknowledgedAt) return null;

  return (
    <div className="handler-section">
      <Label>Acknowledgement</Label>
      <div className="handler-info">
        <div className="alarm-info-item">
          <Label>Acknowledged by</Label>
          <span className="alarm-info-value">
            <UserSignature userId={alarm.acknowledgedBy} />
          </span>
        </div>
        <div className="alarm-info-item">
          <Label>Acknowledged at</Label>
          <span className="alarm-info-value">{formatDate(alarm.acknowledgedAt, timezone)}</span>
        </div>
      </div>
      {alarm?.acknowledgedText && (
        <div className="alarm-info-item">
          <Label>Acknowledgment notes</Label>
          <p className="handler-text-block">{alarm.acknowledgedText}</p>
        </div>
      )}
    </div>
  );
};

const AcknowledgeForm = ({ text, setText, onSubmit }) => (
  <>
    <Label>Acknowledge the alarm</Label>
    <form onSubmit={onSubmit}>
      <Textarea
        name="acknowledgeText"
        value={text}
        onChange={(event) => setText(event.target.value)}
        placeholder="Add acknowledgment notes..."
        rows={4}
      />
      <div className="handler-action-buttons">
        <Button
          type="submit"
          activity="primary"
          disabled={!text.trim()}
        >
          Acknowledge
        </Button>
      </div>
    </form>
  </>
);

const ResolveForm = ({ text, setText, onSubmit }) => (
  <>
    <Label>Resolve the alarm</Label>
    <p className="handler-hint">
      The Latest Severity is Normal, which indicates appropriate actions might have been taken.
    </p>
    <form onSubmit={onSubmit}>
      <Textarea
        name="resolveText"
        value={text}
        onChange={(event) => setText(event.target.value)}
        placeholder="Add resolution notes..."
        rows={4}
      />
      <div className="handler-action-buttons">
        <Button
          type="submit"
          activity="primary"
          disabled={!text.trim()}
        >
          Resolve
        </Button>
      </div>
    </form>
  </>
);

// MAIN-COMPONENT
const AlarmHandlerTab = ({ alarm, onAlarmUpdate, alarmQueryParameters }) => {
  const dispatch = useDispatch();
  const timezone = useSelector(getTimezone);
  const canWriteAlarms = useSelector((state) => {
    const activeSite = getActiveSite(state);
    return getHasPermission(state, 'alarms/Write', { org: activeSite?.org, site: activeSite?.id });
  });

  const [acknowledgeText, setAcknowledgeText] = useState('');
  const [resolveText, setResolveText] = useState('');

  useEffect(() => {
    const userIds = [alarm?.resolvedBy, alarm?.acknowledgedBy].filter(Boolean);
    userIds.forEach((userId) => dispatch(requestUser(userId)));
  }, [dispatch, alarm?.resolvedBy, alarm?.acknowledgedBy]);

  const handleAcknowledge = useCallback(
    (event) => {
      event.preventDefault();
      dispatch(
        updateAlarm(
          alarm.id,
          {
            state: ALARM_STATE.ACKNOWLEDGED,
            acknowledgedText: acknowledgeText,
          },
          { queryParameters: alarmQueryParameters }
        )
      );
      setAcknowledgeText('');
      onAlarmUpdate?.();
    },
    [alarm.id, acknowledgeText, dispatch, onAlarmUpdate, alarmQueryParameters]
  );

  const handleResolve = useCallback(
    (event) => {
      event.preventDefault();
      dispatch(
        updateAlarm(
          alarm.id,
          {
            state: ALARM_STATE.RESOLVED,
            resolvedText: resolveText,
          },
          { queryParameters: alarmQueryParameters }
        )
      );
      setResolveText('');
      onAlarmUpdate?.();
    },
    [alarm.id, resolveText, dispatch, onAlarmUpdate, alarmQueryParameters]
  );

  const isNew = alarm?.state === ALARM_STATE.NEW;
  const isAcknowledged = alarm?.state === ALARM_STATE.ACKNOWLEDGED;
  const isResolved = alarm?.state === ALARM_STATE.RESOLVED;
  const isNormalSeverity = alarm?.latestSeverity?.toLowerCase() === 'normal';

  const severityLabel = alarm?.latestSeverity
    ? `${alarm.latestSeverity.charAt(0).toUpperCase()}${alarm.latestSeverity.slice(1).toLowerCase()} Text`
    : 'Message';

  const showAcknowledgeAction = isNew && canWriteAlarms;
  const showResolveAction = isAcknowledged && isNormalSeverity && canWriteAlarms;

  return (
    <div className="alarm-handler-tab">
      <div className="handler-content">
        {isResolved ? (
          <ResolutionContent
            alarm={alarm}
            timezone={timezone}
          />
        ) : (
          <>
            <MessageSection
              alarm={alarm}
              severityLabel={severityLabel}
            />
            <RecommendationSection alarm={alarm} />
            {isAcknowledged && (
              <AcknowledgementSection
                alarm={alarm}
                timezone={timezone}
              />
            )}
          </>
        )}
      </div>

      {(showAcknowledgeAction ||
        showResolveAction ||
        (isNormalSeverity && !isResolved && canWriteAlarms)) && (
        <div className="handler-action-section">
          {showAcknowledgeAction && !isNormalSeverity && (
            <AcknowledgeForm
              text={acknowledgeText}
              setText={setAcknowledgeText}
              onSubmit={handleAcknowledge}
            />
          )}

          {(showResolveAction || (isNormalSeverity && !isResolved && canWriteAlarms)) && (
            <ResolveForm
              text={resolveText}
              setText={setResolveText}
              onSubmit={handleResolve}
            />
          )}
        </div>
      )}
    </div>
  );
};

export default AlarmHandlerTab;
